// ─────────────────────────────────────────────
// KLOTHIQ — Update KLOTHIQ Bank Balance Modal (FR-022)
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import DatePicker from '@/components/common/DatePicker';
import { useState, useEffect } from 'react';
import { X, Landmark, Loader2, ArrowUpRight, ArrowDownRight, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateBankBalance } from '@/hooks/useAccounts';
import { formatINR } from '@/utils/cashbookCalc';

interface UpdateBankBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  onSuccess?: (newBalance: number) => void;
}

export default function UpdateBankBalanceModal({
  isOpen,
  onClose,
  currentBalance,
  onSuccess,
}: UpdateBankBalanceModalProps) {
  const today = new Date().toISOString().split('T')[0] ?? '';
  const [newBalanceInput, setNewBalanceInput] = useState('');
  const [date, setDate] = useState(today);
  const [notes, setNotes] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const updateMutation = useUpdateBankBalance();

  useEffect(() => {
    if (isOpen) {
      setNewBalanceInput(currentBalance > 0 ? currentBalance.toFixed(2) : '');
      setDate(today);
      setNotes('');
      setValidationError(null);
    }
  }, [isOpen, currentBalance, today]);

  if (!isOpen) return null;

  const parsedNewBalance = parseFloat(newBalanceInput);
  const isValidNumber = !isNaN(parsedNewBalance) && parsedNewBalance >= 0;
  const diff = isValidNumber ? parsedNewBalance - currentBalance : 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!newBalanceInput.trim() || isNaN(parsedNewBalance)) {
      setValidationError('Please enter a valid bank balance.');
      return;
    }
    if (parsedNewBalance < 0) {
      setValidationError('Bank balance must be greater than or equal to 0.');
      return;
    }
    if (!date) {
      setValidationError('Effective date is required.');
      return;
    }
    if (date > today) {
      setValidationError('Effective date cannot be in the future.');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        new_balance: parsedNewBalance,
        date,
        notes: notes.trim() || undefined,
      });

      toast.success(
        `KLOTHIQ Bank balance updated to ${formatINR(parsedNewBalance)}`
      );
      onSuccess?.(parsedNewBalance);
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to update bank balance';
      setValidationError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs" data-field-guide-scope>
      <div className="obsidian-panel relative w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl animate-in fade-in zoom-in-95">
        <button
          type="button"
          onClick={onClose}
          disabled={updateMutation.isPending}
          className="absolute right-4 top-4 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400">
            <Landmark className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-start justify-between gap-3 pr-8">
              <h2 className="text-lg font-semibold text-foreground">
                {currentBalance > 0 ? 'Update KLOTHIQ Bank Balance' : 'Set KLOTHIQ Bank Balance'}
              </h2>
              <UserGuide compact />
            </div>
            <p className="text-xs text-muted-foreground">
              Enter the total available balance in your KLOTHIQ Bank account based on your latest bank statement and expenses. All customer sales are included; partner capital is tracked separately.
            </p>
          </div>
        </div>

        {/* Current Balance Card */}
        <div className="mt-5 rounded-lg border border-border/80 bg-muted/40 p-3.5 flex items-center justify-between">
          <div>
            <span className="text-xs font-medium text-muted-foreground">Current Available Balance</span>
            <div className="mt-0.5 text-base font-bold text-foreground">
              {formatINR(currentBalance)}
            </div>
          </div>
          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            KLOTHIQ Bank
          </span>
        </div>

        {validationError && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-destructive/10 p-2.5 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{validationError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="new-bank-balance" className="text-xs font-medium text-foreground">
              Total KLOTHIQ Bank Balance (₹) <span className="text-destructive">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                ₹
              </span>
              <Input
                id="new-bank-balance"
                type="number"
                min="0"
                step="0.01"
                placeholder="e.g. 50000.00"
                value={newBalanceInput}
                onChange={(e) => {
                  setNewBalanceInput(e.target.value);
                  setValidationError(null);
                }}
                className="w-full rounded-md border border-border bg-background py-2 pl-7 pr-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                disabled={updateMutation.isPending}
                autoFocus
                helpLabel="Total KLOTHIQ Bank Balance (₹) *"
              />
            </div>

            {/* Difference Indicator */}
            {isValidNumber && (
              <div className="pt-1">
                {Math.abs(diff) < 0.01 ? (
                  <span className="text-[11px] text-muted-foreground">
                    No change in bank balance
                  </span>
                ) : diff > 0 ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                    <ArrowUpRight className="h-3 w-3" />
                    +{formatINR(diff)} adjustment will be credited to the ledger
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-600 dark:text-rose-400">
                    <ArrowDownRight className="h-3 w-3" />
                    −{formatINR(Math.abs(diff))} adjustment will be debited from the ledger
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="bank-balance-date" className="text-xs font-medium text-foreground">
              Effective Date <span className="text-destructive">*</span>
            </label>
            <DatePicker
              clearable={false}
              aria-label="Effective date"
              id="bank-balance-date"
              max={today}
              today={today}
              value={date}
              onChange={(nextDate) => {
                setDate(nextDate ?? '');
                setValidationError(null);
              }}
              disabled={updateMutation.isPending}
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="bank-balance-notes" className="text-xs font-medium text-foreground">
              Notes / Reason <span className="text-muted-foreground text-[11px]">(optional)</span>
            </label>
            <Input
              id="bank-balance-notes"
              type="text"
              placeholder="e.g. Bank statement balance verification, opening setup"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={updateMutation.isPending}
              helpLabel="Notes / Reason (optional)"
            />
          </div>

          <div className="flex justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={updateMutation.isPending}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
            >
              {updateMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {currentBalance > 0 ? 'Update Balance' : 'Set Balance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
